import ResourceEditTabBase from "./ResourceEditTabBase.jsx";
import {WIDGET_KEYS} from "../../../other/constants/WidgetKeys.jsx";
import {
  EDIT_MESSAGES,
  RESOURCE_LABEL_MAX_LENGTH,
  SETTING_TAB_WIDGET_KEYS
} from "../../../other/constants/OtherKeys.jsx";
import {EDITOR_KEYS} from "../../../other/constants/EditorKeys.jsx";
import { loadUserAvatars } from "../../../other/utils/utils.jsx";
import { STORE_KEYS } from "../../../other/constants/StoreKeys.jsx";
import CompanyCamField from "@/bryntum-lib/widget/fields/CompanyCamField.jsx";

export default class ResourceGeneralTab extends ResourceEditTabBase {
  static get $name() {
    return "ResourceGeneralTab";
  }

  // Factoryable type name
  static get type() {
    return "resourcegeneraltabV2";
  }

  get swivuDeepLinkSlug() {
    return "general";
  }

  static get configurable() {
    return {
      title: "General",
      tab : {
        icon: "fa-regular fa-memo w-4 h-4"
      },
      autoUpdateFields: true,
      contentElementCls: "gap-[1em]",
      layoutStyle: {
        justifyContent: "space-between",
        flexDirection: "column",
        flexWrap: "nowrap"
      },
      items: {
        fieldsContainer: {
          scrollable: true,
          defaults: {
            labelWidth: "7em",
            cls: "task-editor-v2-field",
          },
          cls: "flex",
          contentElementCls: "task-editor-v2-content gap-[0.7em]",
          type: "container",
          items: {
            note: {
              type: "text",
              cls: "w-full !flex-[1_0_100%] !@md:flex-[1_0_100%] py-2 border-0 border-l-4 border-solid border-[#e0e0e0] dark:border-[#333] pl-[6px] bg-[#fafafa] dark:bg-[#252525] dark:text-[#a5a5a5]",
              content: "Resources can be people, tools, trucks, equipment, facilities, permits, and more.",
            },
            labelField: {
              name: "name",
              type: WIDGET_KEYS.duplicateEntityHelper,
              storeKey: STORE_KEYS.resourceStore,
              label: "Resource ID",
              placeholder: "12-char ID for quick recognition on Swivu HQ",
              cls: "w-1/2 !flex-[1_0_49%] !@md:flex-[1_0_49%]",
              maxLength: RESOURCE_LABEL_MAX_LENGTH
            },
            descriptionField: {
              type: "text",
              name: "description",
              label: "Description",
              placeholder: "Required description for desktop and mobile users",
              cls: "w-1/2 !flex-[1_0_49%] !@md:flex-[1_0_49%]",
              onChange: "up.onRecordChange"
            },
            locationField: {
              name: "location",
              type: "text",
              label: "Location",
              cls: "w-full !flex-[1_0_100%] !@md:flex-[1_0_100%]",
              onChange: "up.onRecordChange"
            },
            companyCamContainer: {
              type: CompanyCamField.type,
              cls: "w-[100%] !flex-initial !flex-[1_0_100%] !@md:flex-[1_0_100%]"
            },
            statusField: {
              type: WIDGET_KEYS.settingsCombo,
              name: "statusId",
              label: "Status",
              valueField: "id",
              displayField: "tagValue",
              cls: "w-1/2 !flex-[1_0_49%] !@md:flex-[1_0_49%]",
              clearable: true,
              editMessage: EDIT_MESSAGES.status,
              tabName: SETTING_TAB_WIDGET_KEYS.resourceStatusTab,
              onSwivuEditMessage: "onClickSwivuEditMessage",
              onChange: "up.onRecordChange"
            },
            blackOutField: {
              name: "hideAgenda",
              label: "Blackout",
              cls: "w-1/2 !flex-[1_0_49%] !@md:flex-[1_0_49%]",
              type: "text",
              editable: false, // Make input field not editable
              triggers: {
                blackoutButton: {
                  handler: "up.onToggleBlackout"
                }
              },
              onChange: "up.onUpdateBlackoutState"
            },
            categoryField: {
              type: WIDGET_KEYS.settingsCombo,
              name: "categoryId",
              label: "Category",
              valueField: "id",
              displayField: "tagValue",
              clearable: true,
              cls: "w-1/2 !flex-[1_0_49%] !@md:flex-[1_0_49%]",
              editMessage: EDIT_MESSAGES.category,
              tabName: SETTING_TAB_WIDGET_KEYS.resourceCategoryTab,
              onSwivuEditMessage: "onClickSwivuEditMessage",
              onChange: "up.onRecordChange"
            },
            tagsField: {
              type: WIDGET_KEYS.settingsCombo,
              name: "tagsId",
              label: "Tags",
              valueField: "id",
              displayField: "tagValue",
              multiSelect: true,
              hidePickerOnSelect: true,
              cls: "w-1/2 !flex-[1_0_49%] !@md:flex-[1_0_49%]",
              editMessage: EDIT_MESSAGES.tag,
              tabName: SETTING_TAB_WIDGET_KEYS.resourceTagsTab,
              onSwivuEditMessage: "onClickSwivuEditMessage",
              onChange: "up.onRecordChange"
            },
            iconField: {
              type: WIDGET_KEYS.fieldIcon,
              name: "iconId",
              label: "Icon",
              valueField: "id",
              displayField: "tagValue",
              cls: "w-1/2 !flex-[1_0_49%] !@md:flex-[1_0_49%]",
              clearable: true,
              placeholder: "Icon colors are inherited from Team Colors.",
              editable: false,
              editMessage: EDIT_MESSAGES.icon,
              tabName: SETTING_TAB_WIDGET_KEYS.iconsTab,
              onSwivuEditMessage: "onClickSwivuEditMessage",
              onChange: "up.onRecordChange"
            },
            teamField: {
              type: WIDGET_KEYS.settingsCombo,
              name: "teamId",
              label: "Team",
              valueField: "id",
              displayField: "name",
              clearable: true,
              cls: "w-1/2 !flex-[1_0_49%] !@md:flex-[1_0_49%]",
              editMessage: EDIT_MESSAGES.team,
              tabName: SETTING_TAB_WIDGET_KEYS.teamsTab,
              onSwivuEditMessage: "onClickSwivuEditMessage",
              onChange: "up.onRecordChange"
            },
            typeField: {
              type: WIDGET_KEYS.settingsCombo,
              name: "typeId",
              label: "Type",
              valueField: "id",
              displayField: "description",
              cls: "w-1/2 !flex-[1_0_49%] !@md:flex-[1_0_49%]",
              clearable: true,
              editMessage: EDIT_MESSAGES.type,
              tabName: SETTING_TAB_WIDGET_KEYS.resourceTypeTab,
              onSwivuEditMessage: "onClickSwivuEditMessage",
              onChange: "up.onRecordChange"
            },
            subType: {
              name: "jobTitle",
              label: "Sub Type",
              cls: "w-1/2 !flex-[1_0_49%] !@md:flex-[1_0_49%]",
              type: "text",
              onSwivuEditMessage: "onClickSwivuEditMessage",
              onChange: "up.onRecordChange"
            },
          }
        },
        bbarContainer: {
          type: "container",
          contentElementCls: "shrink-0",
          layoutStyle: {
            justifyContent: "flex-end",
            flexWrap: "nowrap",
            alignItems: "center"
          },
          items: {
            activeUsers: {
              type: "displayfield",
              contentElementCls: "!flex-auto !self-center flex-row-reverse gap-[0.5em] overflow-y-auto swivu-recipients-cell",
              html: null
            },
            saveBtn: {
              text: "Save", cls: "text-[#048ded] shrink-0", type: "button", width: "8em", onClick: "up.onClickSaveBtn"
            },
          }
        },
      }
    };
  }


  onToggleBlackout() {
    this.record.hideAgenda = !this.record.hideAgenda;
  }

  onUpdateBlackoutState({ value }) {
    const { blackOutField } = this.widgetMap;
    blackOutField.triggers.blackoutButton.cls = {
      "fa-regular fa-lightbulb-slash swivu-input-trigger": value,
      "fa-light fa-lightbulb-on swivu-input-trigger": !value,
      "!text-[#0194fb]": !value,
      "!text-[#ff4646]": value,
    };
    if (value) {
      blackOutField.labelCls = "line-through"
    } else {
      blackOutField.labelCls = ""
    }
    /* We are using a timeout here to update input's value after bryntum's internal state is updated, If we don't use timer here our value will be overwritten by bryntum's value which is going to be boolean coming from model's field "hideAgenda" */
    setTimeout(() => {
      blackOutField.input.value = value ? "Don't display this resource on external schedules." : "Display this resource on external schedules."
    });
  }

  loadRecord(record, context, otherParams) {
    const { project } = context;
    const {
      categoryField,
      statusField,
      tagsField,
      typeField,
      teamField,
      iconField,
      labelField,
      companyCamContainer
    } = this.widgetMap;
    labelField.loadRecord(record, context, {
      editor: otherParams.editor,
      onAfterFieldUpdate: this.onRecordChange,
      recordContainer: this
    })
    this.updateFieldsState(record.isPhantom);
    if (record.isPhantom) {
       /* Add listener to check If record is persisted then enable fields */
       const detacher = project.resourceStore.on({
        idChange({ record }) {
          if (record.id === this.record.id) {
            this.updateFieldsState(false);
            detacher();
          }
        },
        thisObj: this,
      })
    }

    categoryField.store = project.resourceTagValues.chain(
      (r) => r.tagSet === "Category"
    );
    statusField.store = project.resourceTagValues.chain(
      (r) => r.tagSet === "Status"
    );
    tagsField.store = project.resourceTagValues.chain(
      (r) => r.tagSet === "Tag"
    );
    iconField.store = project.resourceTagValues.chain(
      (r) => r.tagSet === "Icon"
    );
    typeField.store = project.resourceTypeStore.chain();
    teamField.store = project.teamStore.chain();

    companyCamContainer.loadRecord(record, context, {
      ...otherParams,
      addEntityRecordFromCompanyCamProject: () => {
        this.onRecordChange({ value: this.record.name, source: labelField, userAction: true, valid: true });
      }
    });

    // we need to load record into this tab once all fields get their store first otherwise you'll see integer value in combo field instead of name
    super.loadRecord(record, context);
  }

  onClickSwivuEditMessage({ tabName }) {
    this.editorHandler.openEditor(EDITOR_KEYS.settingGeneralEditor, null, { selectTabByTypeOnOpen: tabName }, { by: "recordType", useExisting: true });
  }

  onActiveUserUpdate(activeUsersData) {
    loadUserAvatars(this.widgetMap.activeUsers, activeUsersData, this.project)
  }

  updateFieldsState (isDisabled = true) {
    const { descriptionField, locationField, statusField, blackOutField, categoryField, tagsField, iconField, teamField, typeField, subType } = this.widgetMap;

    [descriptionField, locationField, statusField, blackOutField, categoryField, tagsField, iconField, teamField, typeField, subType].forEach(field => field.disabled = isDisabled);
  }
}

ResourceGeneralTab.initClass();
